"use client";

import * as React from "react";

import { cn } from "../../lib/utils";
import {
	AGENT_AVATAR_COLORS,
	AgentAvatar,
	type AgentAvatarFinish,
	type AgentAvatarShape,
	type AgentAvatarState,
} from "./agent-avatar";
import { AnimatedNumber } from "./animated-number";
import { Beacon, type BeaconAnimation, type BeaconVariant } from "./beacon";
import { Card } from "./card";
import { Text } from "./text";

/**
 * What the agent is doing, in the product's words.
 *
 * These are not the avatar's own state names. The avatar describes a character (`working`,
 * `asleep`); a card describes an agent to an operator, and "not enabled" is a
 * configuration fact rather than a mood. {@link AVATAR_STATE} maps between the two.
 */
export type AgentCardState =
	| "idle"
	| "queued"
	| "running"
	| "done"
	| "failed"
	| "disabled";

const AVATAR_STATE: Record<AgentCardState, AgentAvatarState> = {
	idle: "idle",
	queued: "idle",
	running: "working",
	done: "done",
	failed: "failed",
	disabled: "asleep",
};

/**
 * The dot's treatment per state.
 *
 * Only `running` breathes. A dot that pulses in every state stops meaning anything, and the
 * point of the breathing one is that it is the only thing on a roster still moving.
 */
const DOT: Record<
	AgentCardState,
	{ variant: BeaconVariant; animation: BeaconAnimation }
> = {
	idle: { variant: "neutral", animation: "none" },
	queued: { variant: "neutral", animation: "none" },
	running: { variant: "neutral", animation: "breathe" },
	done: { variant: "success", animation: "none" },
	failed: { variant: "danger", animation: "none" },
	disabled: { variant: "neutral", animation: "none" },
};

/**
 * Fallback copy, so the common cases need none.
 *
 * `idle` is the weakest of the states because the useful thing to say there is when the agent
 * last ran, which only the caller knows — pass `status="Last ran 10:55"`.
 */
const STATUS_LABEL: Record<AgentCardState, string> = {
	idle: "Idle",
	queued: "Queued",
	running: "Working now",
	done: "Done",
	failed: "Failed",
	disabled: "Not enabled yet",
};

/** The figure's own colour per state, so a disabled card reads as switched off rather than empty. */
const FIGURE_CLASS: Record<AgentCardState, string> = {
	idle: "",
	queued: "",
	running: "",
	done: "",
	failed: "",
	disabled: "text-neutral-400",
};

/**
 * The status label per state. Only the running and settled states are emphasised, so a roster's
 * resting cards stay quiet and the one that is working reads first.
 */
const STATUS_CLASS: Record<AgentCardState, string> = {
	idle: "text-color-body-muted",
	queued: "text-color-body-muted",
	running: "font-semibold text-neutral-600",
	done: "font-semibold text-success-600",
	failed: "font-semibold text-danger-600",
	disabled: "text-color-body-muted",
};

/**
 * Shown in place of the figure for an agent that has never run — deliberately not a zero,
 * which would be a measurement. Matches the placeholder `FileSize` and `ReadOnlyField`
 * already default to, so a missing value looks the same everywhere in the system.
 */
const NO_VALUE = "-";

/**
 * How the card arranges the avatar, the name and the text under them.
 *
 * `inline` puts a small avatar beside the name, for a dense roster where the figure is the
 * headline. `stacked` puts a large avatar above the name, for a carousel where the agent
 * itself is the headline.
 */
export type AgentCardLayout = "inline" | "stacked";

/**
 * The avatar's default size per layout. The inline size matches the source design: large
 * enough to read as a character next to the name. The stacked size matches the Automations
 * roster, where the avatar sits above the name and carries the card.
 */
const AVATAR_SIZE: Record<AgentCardLayout, number> = {
	inline: 26,
	stacked: 56,
};

/** Props for the {@link AgentCard} component. */
export type AgentCardProps = Omit<
	React.HTMLAttributes<HTMLDivElement>,
	"color"
> & {
	/** The agent's name. Truncated rather than wrapped, so every card in a roster is the same height. */
	name: string;
	/**
	 * How the card is arranged. Defaults to `"inline"`: a small avatar beside the name.
	 *
	 * `"stacked"` puts a large avatar above the name and clamps the meta to two lines, held at
	 * two lines' height even when it is shorter. The card also fills its parent's height and
	 * pins the status to the bottom, so every status line in a carousel row sits on one edge.
	 */
	layout?: AgentCardLayout;
	/** The avatar's width and height in pixels. Defaults to `26` inline and `56` stacked. */
	avatarSize?: number;
	/** The agent's silhouette. Carries its identity, so keep it stable per agent. */
	shape: AgentAvatarShape;
	/** Fill for the avatar and the running dot. Defaults to the first of {@link AGENT_AVATAR_COLORS}. */
	color?: string;
	/**
	 * Colour for the status label while running. Defaults to the neutral text colour.
	 *
	 * Separate from `color` because the two have different jobs: a fill only has to be
	 * visible, whereas 12px text has to clear 4.5:1 against white, which several of the
	 * avatar fills do not. Pass a darker tint of `color` to reproduce a design that tints
	 * the label, and check the contrast when you do.
	 */
	accentColor?: string;
	/** What the agent is doing. Defaults to `"idle"`. */
	state?: AgentCardState;
	/**
	 * The headline figure, animated on change. `null` renders {@link NO_VALUE} instead, for
	 * an agent that has no figure yet — which is not the same as one whose figure is zero.
	 */
	value?: number | null;
	/** Maps `value` to the string on screen, e.g. minutes to `"55h 15m"`. */
	formatValue?: (value: number) => string;
	/**
	 * Renders the figure row. Defaults to `true`.
	 *
	 * Turn it off for a card describing an agent the tenant does not have yet, where even
	 * the {@link NO_VALUE} placeholder overstates the case: a dash says "no figure yet" of
	 * an agent that is running, and there is nothing to have a figure for. Also turn it off
	 * on a stacked roster card whose figure the page shows elsewhere. Inline, `meta`
	 * takes the figure's spacing when this is off, so the card keeps its rhythm. Stacked,
	 * `meta` sits directly under the name.
	 */
	showFigure?: boolean;
	/** The line under the figure, e.g. `"saved, from 221 jobs"`. */
	meta?: React.ReactNode;
	/** Overrides the per-state default label, e.g. `"Last ran 10:55"` for an idle agent. */
	status?: React.ReactNode;
	/** Seconds to offset the avatar's ambient loop, so a roster does not blink in unison. */
	delay?: number;
	/** How the avatar is painted, passed to {@link AgentAvatar}. Defaults to `"flat"`. */
	finish?: AgentAvatarFinish;
	/**
	 * Whether the avatar and the running dot move. Defaults to `true`.
	 *
	 * Turn it off where the same agent is already animated elsewhere on the page, so one
	 * agent never moves in two places. The pose and the label still follow `state`.
	 */
	animate?: boolean;
	/**
	 * Applies the hover treatment. Defaults to `false`.
	 *
	 * Opt-in because a card that lights up under the cursor and then does nothing is a
	 * broken promise. Turn it on when you have wrapped the card in a link or a button, so
	 * the semantics and keyboard handling come from a real control rather than from here.
	 */
	interactive?: boolean;
};

/**
 * One background agent on a roster: who it is, what it has saved, and what it is doing.
 *
 * Composed from the design system rather than drawn here — {@link AgentAvatar} for the
 * character, {@link AnimatedNumber} for the figure, {@link Beacon} for the status dot,
 * {@link Text} for the typography and {@link Card} for the surface. The card's own job is
 * the arrangement and the six states.
 *
 * State reaches the reader as text in every case. The avatar and the dot are decorative and
 * hidden from assistive technology, so nothing here is carried by colour or motion alone.
 *
 * @param props.name - The agent's name.
 * @param props.layout - How the card is arranged. Defaults to `"inline"`.
 * @param props.avatarSize - The avatar's size in pixels. Defaults to `26` inline and `56` stacked.
 * @param props.shape - The agent's silhouette.
 * @param props.color - Fill for the avatar and the running dot.
 * @param props.accentColor - Colour for the status label while running. Defaults to neutral.
 * @param props.state - What the agent is doing. Defaults to `"idle"`.
 * @param props.value - The headline figure. `null` renders a placeholder.
 * @param props.formatValue - Maps `value` to the string on screen.
 * @param props.showFigure - Renders the figure row. Defaults to `true`.
 * @param props.meta - The line under the figure.
 * @param props.status - Overrides the per-state default label.
 * @param props.delay - Seconds to offset the avatar's ambient loop.
 * @param props.finish - How the avatar is painted. Defaults to `"flat"`.
 * @param props.animate - Whether the avatar and the running dot move. Defaults to `true`.
 * @param props.interactive - Applies the hover treatment. Defaults to `false`.
 */
function AgentCard({
	name,
	layout = "inline",
	avatarSize,
	shape,
	// Defaulted here rather than left to `AgentAvatar` alone: the avatar and the running dot
	// are meant to be the same colour, and only the avatar has a default of its own, so an
	// omitted `color` would otherwise pair a coloured silhouette with a grey running dot.
	color = AGENT_AVATAR_COLORS[0],
	accentColor,
	state = "idle",
	value,
	formatValue,
	showFigure = true,
	meta,
	status,
	delay,
	finish,
	animate = true,
	interactive = false,
	className,
	...props
}: AgentCardProps) {
	const isDisabled = state === "disabled";
	const isStacked = layout === "stacked";
	const dot = DOT[state];

	const avatar = (
		<AgentAvatar
			shape={shape}
			color={color}
			state={AVATAR_STATE[state]}
			size={avatarSize ?? AVATAR_SIZE[layout]}
			delay={delay}
			finish={finish}
			animate={animate}
			data-testid="agent-card-avatar"
		/>
	);

	const nameClassName = cn(
		"truncate font-semibold",
		isDisabled && "text-neutral-400",
	);

	return (
		<Card
			data-slot="agent-card"
			data-state={state}
			data-layout={layout}
			className={cn(
				// Stacked fills its slide and lays out as a column, so the status row below can
				// take the spare height and every card in a carousel row ends on the same edge.
				isStacked
					? "flex h-full flex-col rounded-xl p-4 shadow-none transition-colors"
					: "rounded-xl px-4 pb-3.5 pt-3 shadow-none transition-colors",
				interactive && "hover:bg-neutral-50",
				isDisabled && "border-dashed bg-neutral-50",
				className,
			)}
			{...props}
		>
			{isStacked ? (
				<>
					{avatar}
					<Text
						as="p"
						variant="body-sm"
						className={cn("mt-3", nameClassName)}
						title={name}
					>
						{name}
					</Text>
				</>
			) : (
				<div className="flex items-center gap-2.5">
					{avatar}
					<Text
						as="span"
						variant="body-sm"
						className={nameClassName}
						title={name}
					>
						{name}
					</Text>
				</div>
			)}

			{showFigure && (
				<div className={cn("mt-2.5", FIGURE_CLASS[state])}>
					{value === null || value === undefined ? (
						<Text as="p" variant="heading-tiny" className="font-semibold">
							{NO_VALUE}
						</Text>
					) : (
						<AnimatedNumber
							as="p"
							variant="heading-tiny"
							className="font-semibold"
							value={value}
							format={formatValue}
						/>
					)}
				</div>
			)}

			{/* Tested for presence rather than truthiness, so a meta of `0` is still rendered.
			    Same distinction the figure makes between a measured zero and nothing yet. */}
			{meta === undefined || meta === null ? null : (
				<Text
					as="p"
					variant="caption-default"
					// Inline, takes the figure's own top spacing when there is no figure, so the
					// card keeps the same rhythm rather than pulling the meta up under the name.
					// Stacked, sits directly under the name, and is clamped to two lines and held
					// at two lines' height, so a short meta does not lift its status line above
					// the rest of the row.
					className={cn(
						"text-color-body-muted",
						showFigure || isStacked ? "mt-0.5" : "mt-2.5",
						isStacked && "line-clamp-2 min-h-[2lh]",
					)}
					data-testid="agent-card-meta"
				>
					{meta}
				</Text>
			)}

			{/* Held at a fixed height so a card does not resize as its label changes length,
			    which on a grid would jostle every card in the row. Stacked, the auto margin
			    pins the row to the foot of the card, and the padding is the gap when there is
			    no spare height. */}
			<div
				className={cn(
					"flex min-h-[17px] items-center gap-1.5",
					isStacked ? "mt-auto pt-2.5" : "mt-2.5",
				)}
			>
				<Beacon
					aria-hidden
					data-testid="agent-card-beacon"
					size="xs"
					variant={dot.variant}
					animation={animate ? dot.animation : "none"}
					// Only the running dot is tinted. A resting dot in the agent's colour would
					// read as active, which is the one thing the dot exists to distinguish.
					color={state === "running" ? color : undefined}
				/>
				<Text
					as="span"
					variant="caption-default"
					className={STATUS_CLASS[state]}
					// Inline so it beats the class, which stays on as the accessible default.
					style={
						state === "running" && accentColor
							? { color: accentColor }
							: undefined
					}
				>
					{status ?? STATUS_LABEL[state]}
				</Text>
			</div>
		</Card>
	);
}

export { AgentCard };
